import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, Spinner } from "react-bootstrap";
import { useAuthUser } from "../../features/auth/authHooks";
import { useAppDispatch } from "../../app/hooks";
import { saveICalEventDetailsAsync } from "../../pages/BookingsPage/bookingsSlice";
import { addStatusToast } from "../StatusToast";

interface ICalEventData {
  id?: string;
  bookingId?: string;
  adults?: number;
  children?: number;
  ownerBookingNotes?: string;
  startDateString?: string;
  endDateString?: string;
  iCalDescription?: string;
  iCalFullDescription?: string;
  iCalLocation?: string;
  iCalExternalUid?: string;
  iCalDistributionChannelName?: string;
  checkInTime?: string;
  checkOutTime?: string;
}

const normalizeNoteText = (value: string) =>
  value
    .toLocaleLowerCase("fi-FI")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const isGeneratedServiceNoteLine = (line: string) => {
  const normalizedLine = normalizeNoteText(line);
  return (
    normalizedLine.includes("sisaltaa liinavaatteet") ||
    normalizedLine.includes("sisaltaa loppusiivouksen")
  );
};

const stripGeneratedServiceNotes = (text?: string) => {
  if (!text) return "";

  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0 && !isGeneratedServiceNoteLine(line))
    .join("\n")
    .trim();
};

interface ICalEventModalProps {
  show: boolean;
  onHide: () => void;
  eventData: ICalEventData | null;
  accommodationId?: string;
  onSave?: () => void;
}

const ICalEventModal: React.FC<ICalEventModalProps> = ({
  show,
  onHide,
  eventData,
  accommodationId,
  onSave,
}) => {
  const { isOwnerUser } = useAuthUser();
  const dispatch = useAppDispatch();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Form state
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [cleaningChecked, setCleaningChecked] = useState(false);
  const [linenChecked, setLinenChecked] = useState(false);
  const [additionalInfo, setAdditionalInfo] = useState("");
  const [checkInTime, setCheckInTime] = useState("16:00");
  const [checkOutTime, setCheckOutTime] = useState("12:00");
  const hasExistingBookingDetails =
    !!eventData?.ownerBookingNotes?.trim() ||
    (eventData?.adults ?? 0) > 0 ||
    (eventData?.children ?? 0) > 0 ||
    !!eventData?.checkInTime ||
    !!eventData?.checkOutTime;

  // Reset form when modal opens
  useEffect(() => {
    if (show) {
      const ownerNotes = eventData?.ownerBookingNotes || "";
      const normalizedOwnerNotes = normalizeNoteText(ownerNotes);
      const hasLinen = normalizedOwnerNotes.includes("sisaltaa liinavaatteet");
      const hasCleaning = normalizedOwnerNotes.includes(
        "sisaltaa loppusiivouksen",
      );
      const cleanedOwnerNotes = stripGeneratedServiceNotes(ownerNotes);

      setIsEditing(isOwnerUser && hasExistingBookingDetails);
      setAdults(
        eventData?.adults && eventData.adults > 0 ? eventData.adults : 1,
      );
      setChildren(
        eventData?.children !== undefined && eventData.children >= 0
          ? eventData.children
          : 0,
      );

      setCleaningChecked(hasCleaning);
      setLinenChecked(hasLinen);
      setAdditionalInfo(cleanedOwnerNotes);
      setCheckInTime(eventData?.checkInTime || "16:00");
      setCheckOutTime(eventData?.checkOutTime || "12:00");
    }
  }, [show, eventData, hasExistingBookingDetails, isOwnerUser]);

  const handleSave = async () => {
    const blockedPeriodId = eventData?.id ?? eventData?.bookingId;
    if (!blockedPeriodId || !accommodationId) return;

    setIsSaving(true);

    const numberOfPeople = adults + children;
    const noteParts: string[] = [];
    if (linenChecked) {
      noteParts.push(`Sisältää liinavaatteet ${numberOfPeople} kpl`);
    }
    if (cleaningChecked) {
      noteParts.push("Sisältää loppusiivouksen");
    }
    const cleanedAdditionalInfo = stripGeneratedServiceNotes(additionalInfo);
    if (cleanedAdditionalInfo) {
      noteParts.push(cleanedAdditionalInfo);
    }
    const noteText = noteParts.join("\n").trim();

    const data = {
      blockedPeriodId,
      accommodationId: accommodationId,
      adults: adults,
      children: children,
      ownerBookingNotes: noteText,
      checkInTime,
      checkOutTime,
    };

    try {
      const result = await dispatch(saveICalEventDetailsAsync(data));
      if (result.meta.requestStatus === "fulfilled") {
        dispatch(
          addStatusToast({
            Message: "iCal -tapahtuman tiedot tallennettu!",
          }),
        );
        if (onSave) {
          onSave();
        }
        onHide();
      }
    } catch (error) {
      console.error("Error saving iCal event details:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setIsEditing(false);
    onHide();
  };
  return (
    <Modal show={show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>iCal -tapahtuma</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {eventData && (
          <div>
            {eventData.startDateString && eventData.endDateString && (
              <div className="mb-3">
                <strong>Ajankohta:</strong>
                <p>
                  {eventData.startDateString} {eventData.checkInTime || ""} -{" "}
                  {eventData.endDateString} {eventData.checkOutTime || ""}
                </p>
              </div>
            )}
            {(eventData.iCalDescription ||
              eventData.iCalFullDescription ||
              eventData.iCalLocation) && (
              <div className="mb-3">
                <strong>Kuvaus:</strong>
                {eventData.iCalDescription && (
                  <p>{eventData.iCalDescription}</p>
                )}
                {eventData.iCalFullDescription && (
                  <p>{eventData.iCalFullDescription}</p>
                )}
                {eventData.iCalLocation && <p>{eventData.iCalLocation}</p>}
              </div>
            )}
            {eventData.iCalExternalUid && (
              <div className="mb-3">
                <strong>Ulkoinen tunniste (UID):</strong>
                <p>{eventData.iCalExternalUid}</p>
              </div>
            )}
            {eventData.iCalDistributionChannelName && (
              <div className="mb-3">
                <strong>Kanava:</strong>
                <p>{eventData.iCalDistributionChannelName}</p>
              </div>
            )}

            {!isEditing && hasExistingBookingDetails && (
              <div className="mb-1">
                <strong>Varaustiedot:</strong>
                <p className="mb-1">
                  Henkilöitä: {adults} + {children}
                </p>
                <p className="mb-1">
                  Sisäänkirjautuminen: {checkInTime || "-"}
                  <br />
                  Uloskirjautuminen: {checkOutTime || "-"}
                </p>
                {(cleaningChecked || linenChecked) && (
                  <p className="mb-1">
                    Palvelut:
                    {cleaningChecked ? " Loppusiivous" : ""}
                    {cleaningChecked && linenChecked ? "," : ""}
                    {linenChecked
                      ? " Liinavaatteet " + (adults + children) + " kpl"
                      : ""}
                  </p>
                )}
                {additionalInfo && <p className="mb-0">{additionalInfo}</p>}
              </div>
            )}

            {isOwnerUser && isEditing && (
              <Form className="mt-4">
                <div className="d-grid gap-16">
                  <Row>
                    <Col>
                      <Form.Group>
                        <Form.Label>Aikuisia</Form.Label>
                        <Form.Select
                          value={adults}
                          onChange={(e) => setAdults(+e.target.value)}
                        >
                          <option value="1">1</option>
                          <option value="2">2</option>
                          <option value="3">3</option>
                          <option value="4">4</option>
                          <option value="5">5</option>
                          <option value="6">6</option>
                          <option value="7">7</option>
                          <option value="8">8</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col>
                      <Form.Group>
                        <Form.Label>Lapsia</Form.Label>
                        <Form.Select
                          value={children}
                          onChange={(e) => setChildren(+e.target.value)}
                        >
                          <option value="0">0</option>
                          <option value="1">1</option>
                          <option value="2">2</option>
                          <option value="3">3</option>
                          <option value="4">4</option>
                          <option value="5">5</option>
                          <option value="6">6</option>
                          <option value="7">7</option>
                          <option value="8">8</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col>
                      <Form.Group>
                        <Form.Label>Saapumisaika</Form.Label>
                        <Form.Control
                          type="time"
                          value={checkInTime}
                          onChange={(e) => setCheckInTime(e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col>
                      <Form.Group>
                        <Form.Label>Lähtöaika</Form.Label>
                        <Form.Control
                          type="time"
                          value={checkOutTime}
                          onChange={(e) => setCheckOutTime(e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group>
                    <Form.Label>Palvelut</Form.Label>
                    <Form.Check
                      label="Loppusiivous"
                      className="mb-8"
                      id="finalcleaning"
                      checked={cleaningChecked}
                      onChange={() => setCleaningChecked(!cleaningChecked)}
                    />
                    <Form.Check
                      label="Liinavaatteet"
                      id="lining"
                      checked={linenChecked}
                      onChange={() => setLinenChecked(!linenChecked)}
                    />
                  </Form.Group>

                  <Form.Group>
                    <Form.Label>Lisätiedot</Form.Label>
                    <Form.Control
                      as="textarea"
                      value={additionalInfo}
                      onChange={(e) => setAdditionalInfo(e.target.value)}
                      rows={3}
                    />
                  </Form.Group>
                </div>
              </Form>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        {isSaving ? (
          <>
            <Spinner animation="border" size="sm" />
            <span className="ms-2">Tallennetaan...</span>
          </>
        ) : (
          <>
            {isOwnerUser && !isEditing && (
              <Button variant="primary" onClick={() => setIsEditing(true)}>
                {hasExistingBookingDetails
                  ? "Päivitä varaustiedot"
                  : "Lisää varaustiedot"}
              </Button>
            )}
            {isOwnerUser && isEditing && (
              <>
                <Button
                  variant="outline-blue"
                  onClick={() => setIsEditing(false)}
                >
                  Peruuta muutokset
                </Button>
                <Button variant="primary" onClick={handleSave}>
                  Tallenna
                </Button>
              </>
            )}
            {(!isOwnerUser || !isEditing) && (
              <Button variant="secondary" onClick={handleClose}>
                Sulje
              </Button>
            )}
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default ICalEventModal;
export { ICalEventModal };
